/**
 * Schedule Suggestions Dialog
 *
 * Shows AI-suggested time slots as cards with reasoning and accept buttons.
 * Includes loading state while AI processes.
 */

import { Loader2, Zap, Calendar, Clock } from 'lucide-react';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import type { AiScheduleSuggestion, SchedulableTaskDetail } from '@/lib/api';
import type { TimeFormat } from '../types';

interface ScheduleSuggestionsDialogProps {
  open: boolean;
  onClose: () => void;
  task: SchedulableTaskDetail | null;
  suggestions: AiScheduleSuggestion[];
  loading: boolean;
  onAccept: (suggestionIndex: number) => void;
  timeFormat?: TimeFormat;
}

function formatSlotTime(iso: string, timeFormat: TimeFormat = '12h'): string {
  const d = new Date(iso);
  return d.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
    hour12: timeFormat === '12h',
  });
}

function formatDuration(startAt: string, endAt: string): string {
  const diffMs = new Date(endAt).getTime() - new Date(startAt).getTime();
  const minutes = Math.round(diffMs / 60000);
  if (minutes < 60) return `${minutes}m`;
  const hours = Math.floor(minutes / 60);
  const remainingMins = minutes % 60;
  return remainingMins > 0 ? `${hours}h ${remainingMins}m` : `${hours}h`;
}

function getScoreColor(score: number): string {
  if (score >= 0.8) return 'text-green-600 dark:text-green-400';
  if (score >= 0.5) return 'text-yellow-600 dark:text-yellow-400';
  return 'text-orange-600 dark:text-orange-400';
}

export function ScheduleSuggestionsDialog({
  open,
  onClose,
  task,
  suggestions,
  loading,
  onAccept,
  timeFormat = '12h',
}: ScheduleSuggestionsDialogProps) {
  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose()}>
      <DialogContent className="max-w-lg">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2">
            <Zap className="h-5 w-5 text-primary" />
            AI Scheduling Suggestions
          </DialogTitle>
        </DialogHeader>

        {task && (
          <div className="text-sm text-muted-foreground mb-2">
            Finding the best time for{' '}
            <span className="font-medium text-foreground">{task.title}</span> (
            {task.estimatedMinutes}m, {task.priority} priority)
          </div>
        )}

        {loading && (
          <div className="flex flex-col items-center justify-center py-12 gap-3">
            <Loader2 className="h-8 w-8 animate-spin text-primary" />
            <p className="text-sm text-muted-foreground">Analyzing your calendar...</p>
          </div>
        )}

        {!loading && suggestions.length === 0 && (
          <div className="text-center py-8 text-muted-foreground">
            <p>No suggestions available. Try adjusting the task parameters.</p>
          </div>
        )}

        {!loading && suggestions.length > 0 && (
          <div className="space-y-3">
            {suggestions.map((suggestion, index) => (
              <div
                key={index}
                className="border rounded-lg p-3 hover:border-primary/50 transition-colors"
              >
                <div className="flex items-start justify-between gap-3">
                  <div className="flex-1">
                    <div className="flex items-center gap-2 text-sm font-medium">
                      <Calendar className="h-3.5 w-3.5 text-muted-foreground" />
                      {formatSlotTime(suggestion.startAt, timeFormat)}
                    </div>
                    <div className="flex items-center gap-2 mt-1 text-xs text-muted-foreground">
                      <Clock className="h-3 w-3" />
                      {formatDuration(suggestion.startAt, suggestion.endAt)}
                      <span className={`ml-1 font-medium ${getScoreColor(suggestion.score)}`}>
                        {Math.round(suggestion.score * 100)}% match
                      </span>
                    </div>
                    {suggestion.reasoning && (
                      <p className="text-xs text-muted-foreground mt-1.5">{suggestion.reasoning}</p>
                    )}
                  </div>

                  <Button
                    size="sm"
                    variant={index === 0 ? 'default' : 'outline'}
                    onClick={() => onAccept(index)}
                  >
                    Accept
                  </Button>
                </div>
              </div>
            ))}
          </div>
        )}
      </DialogContent>
    </Dialog>
  );
}
